import type {
  ClaimBoundarySummary,
  WorkflowClaimReadinessResponse,
  WorkflowImpactSummary,
} from '../services/reportsApi';

export function buildEffectivenessClaimLabelFromBoundary(
  boundary?: ClaimBoundarySummary | null,
): string {
  if (!boundary) return 'Not allowed';
  if (boundary.exposure_reduction_claimed) return 'Exposure reduction may be claimed';
  if (boundary.outcome_claimed) return 'Outcome may be claimed';
  return 'Not allowed';
}

export function hasCanonicalClaimReadinessSummary(
  claimReadiness?: Partial<WorkflowClaimReadinessResponse> | null,
): boolean {
  if (!claimReadiness || typeof claimReadiness !== 'object') return false;
  return (Array.isArray(claimReadiness.controls) && claimReadiness.controls.length > 0)
    || Number(claimReadiness.total_count || 0) > 0
    || Boolean(String(claimReadiness.claim_boundary?.wording || '').trim());
}

export function resolveMeasurementClaimSummaryLabel(args: {
  claimReadiness?: Partial<WorkflowClaimReadinessResponse> | null;
  impactSummary?: WorkflowImpactSummary | null;
  legacyEffectivenessClaimAllowed?: boolean;
  concise?: boolean;
}): string {
  const concise = Boolean(args.concise);
  const notClaimed = concise ? 'Effectiveness not claimed' : 'No corrective-action effectiveness claim yet.';
  const supported = 'Effectiveness supported';

  if (hasCanonicalClaimReadinessSummary(args.claimReadiness)) {
    const boundary = args.claimReadiness?.claim_boundary;
    if (boundary?.exposure_reduction_claimed || boundary?.outcome_claimed) {
      return concise ? supported : buildEffectivenessClaimLabelFromBoundary(boundary);
    }
    return notClaimed;
  }

  const claimStatus = String(args.impactSummary?.effectiveness_claim_status || '').trim().toLowerCase();
  if (claimStatus === 'validated_effectiveness' || claimStatus === 'effectiveness_supported' || claimStatus === 'claimed') {
    return supported;
  }
  if (
    claimStatus === 'not_claimed'
    || claimStatus === 'not_claimable_yet'
    || claimStatus === 'directional_only'
    || claimStatus === 'observed_change_only'
  ) {
    return notClaimed;
  }
  return notClaimed;
}

export function isEffectivenessClaimAllowedFromBoundary(
  boundary?: ClaimBoundarySummary | null,
): boolean {
  return Boolean(boundary?.exposure_reduction_claimed || boundary?.outcome_claimed);
}
